import {css, StyleSheet} from 'aphrodite';
import Fuse from 'fuse.js';
import React, {ForwardedRef, forwardRef, memo, useEffect, useMemo, useState} from 'react';
import {debounce} from 'ts-debounce';

import {
  getHeaderGroupDropdownSections,
  getInsertGroupDropdownSections,
  NoteToolbarProps,
} from 'notebook/notes/core-ui/NoteToolbar';
import AttachDropdownClass from 'ui-library/attach-dropdown/AttachDropdown';
import AttachDropdown from 'ui-library/attach-dropdown/AttachDropdown';
import {AttachDropdownItem, OptionHeight} from 'ui-library/attach-dropdown/AttachDropdownConstants';
import {
  RenderAttachDropdownOptionInfo,
  SelectAttachment,
} from 'ui-library/attach-dropdown/AttachDropdownOptionsMenu';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

export interface SlashSearchDropdownProps {
  slashSignBox: {
    top: number;
    left: number;
    bottom: number;
  };
  close: () => void;
  containerWidth: number;
  containerHeight: number;
  containerIsFullScreen: boolean;
  fileId: string;
  query: string;
  selectAttachment: SelectAttachment;
  toolbarProps: NoteToolbarProps;
}

type FuseResultOption = Fuse.FuseResult<AttachDropdownItem>;

const SLASH_SEARCH_DROPDOWN_WIDTH = 160;
const SLASH_SEARCH_DROPDOWN_MARGIN = 20;

const DEBOUNCE_MS = 50;

/**
 * Forwards a Ref so that we can imperatively handle the keydown event from the notebook
 */
const SlashSearchDropdown = forwardRef(
  (
    {
      query,
      containerHeight,
      containerWidth,
      containerIsFullScreen,
      slashSignBox,
      selectAttachment,
      close,
      toolbarProps,
    }: SlashSearchDropdownProps,
    ref: ForwardedRef<AttachDropdown>
  ): JSX.Element => {
    const items: Array<AttachDropdownItem> = useMemo(
      () =>
        getInsertGroupDropdownSections()
          .concat(getHeaderGroupDropdownSections())
          .flat()
          .filter(
            (option) =>
              (!option.visible || option.visible(toolbarProps)) &&
              (!option.enabled || option.enabled(toolbarProps))
          )
          .map(
            (option) =>
              ({
                name: option.plainText!,
                searchable: option.plainText!,
                item_id: typeof option.format === 'function' ? option.format(toolbarProps) : option.format,
                item: {iconId: option.icon},
              }) satisfies AttachDropdownItem
          ),
      [toolbarProps]
    );
    const fuse = useMemo(
      () =>
        new Fuse(items, {
          threshold: 0.3,
          findAllMatches: true,
          ignoreLocation: false,
          keys: ['searchable'],
        }),
      [items]
    );
    const [results, setResults] = useState<Array<AttachDropdownItem>>(items);

    const doSlashSearch = useMemo(
      () =>
        debounce((query: string): void => {
          if (query) {
            const filteredResults: Array<AttachDropdownItem> = fuse
              .search(query)
              .map((fuseResult: FuseResultOption) => fuseResult.item);
            setResults(filteredResults);
          }
        }, DEBOUNCE_MS),
      [fuse]
    );

    useEffect(() => {
      doSlashSearch(query);
    }, [doSlashSearch, query]);

    // Measurements
    const maxLeft = containerWidth - SLASH_SEARCH_DROPDOWN_WIDTH - SLASH_SEARCH_DROPDOWN_MARGIN;
    let dropUp;
    if (containerIsFullScreen) {
      // Drop down unless doing so would go past the bottom of the editor
      const maxTop = containerHeight - AttachDropdownClass.MAX_HEIGHT - SLASH_SEARCH_DROPDOWN_MARGIN;
      dropUp = slashSignBox.bottom > maxTop;
    } else {
      // Drop whichever way there is more space available
      const availableSpaceAbove = slashSignBox.top;
      const availableSpaceBelow = containerHeight - slashSignBox.bottom;
      dropUp = availableSpaceAbove > availableSpaceBelow;
    }

    return (
      <AttachDropdown
        ref={ref}
        dropup={dropUp}
        resultsRequireQuery={false}
        emptyQueryMessage="Empty query message"
        noResultsMessage="No results found"
        query={query}
        results={results}
        selectAttachment={selectAttachment}
        searching={false}
        width={SLASH_SEARCH_DROPDOWN_WIDTH}
        close={close}
        defaultOptionSelected={true}
        style={{
          left: Math.min(slashSignBox.left, maxLeft),
          top: dropUp ? slashSignBox.top : slashSignBox.bottom,
        }}
        getRenderOptionInfo={(option: AttachDropdownItem): RenderAttachDropdownOptionInfo => ({
          optionContent: (
            <div className={css(styles.slashDropdownOptionContainer)}>
              <div className={css(styles.slashDropdownOption)}>
                {option.item.iconId && (
                  <BenchlingIcon icon={option.item.iconId} className="u-small-spacing-right" />
                )}
                {option.name}
              </div>
            </div>
          ),
          hoverable: true,
          clickable: true,
        })}
        height={OptionHeight.MEDIUM}
      />
    );
  }
);

export default memo(SlashSearchDropdown);

const styles = StyleSheet.create({
  slashDropdownOptionContainer: {display: 'table', width: '100%', height: '100%'},
  slashDropdownOption: {
    display: 'table-cell',
    verticalAlign: 'middle',
    width: 'inherit',
    paddingLeft: '10px',
    paddingRight: '10px',
  },
});
